import {
  DuplicateChoice,
  type DuplicateSource,
  directDuplicateTag,
  duplicateAsks,
} from '@/components/admin/versioned/duplicate-menu';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { ActionMenu } from '@/components/ui/action-menu';
import { Button } from '@/components/ui/button';
import { useIsVitrine } from '@/lib/use-instance';
import type { VersionedWording } from '@/lib/versioned-labels';
import { usageSentence, versionedDay } from '@/lib/versioned-labels';
import { VITRINE_WORDING } from '@/lib/vitrine';
import type { VersionTag, VersionedCard } from '@matter_extractor/shared';
import { ArrowUp, ChevronRight, Copy } from 'lucide-react';
import { type ReactNode, useState } from 'react';

/**
 * One card of a library (refonte modulaire §4).
 *
 * Five facts, and each is one an operator would otherwise open the editor to
 * learn: what it is called, where the two pointers sit, how widely it is used,
 * what its own facets say, and when it last moved. Nothing derived beyond that
 * - no score, no ratio.
 *
 * ## The facets are the object's SMALLEST contribution
 *
 * A criterion appends « 3 exemples ». They arrive as a list of already-worded
 * strings, joined to the usage sentence by the same middot: the layer decides
 * where the line sits and how it is inked, the object decides what it says. A
 * slot taking a node would have let an object put a control on a card that is
 * entirely a link.
 *
 * ## And two slots for the cards a sentence cannot carry (issue #42)
 *
 * §15 draws the persona's card in a variant of its own - a round avatar, a
 * subtitle, and a MINI-GAUGE of the involvement scale - and none of those three
 * is a string. `renderIdentity` replaces what stands beside the chevron;
 * `renderExtra` inserts one block between that row and the pastilles. Both are
 * OPTIONAL and both default to what this file rendered before them, so the
 * criterion's card emits the same nodes it always did, in the same order - the
 * property `criteres.spec.ts` and the capture of that screen both rest on.
 *
 * Two slots and not one: they land on either side of a row, and a single slot
 * covering both would have handed an object the whole head of the card, which
 * is where the chevron and the greyed-out state live.
 *
 * ## And a THIRD, for the foot of the card (issue #55)
 *
 * §15 and the mockup's README group the experience's foot: « le pied de carte
 * groupe l'usage, l'avertissement et la date ». Two of those three are the
 * layer's already; the one that is not is the warning line a RETIRED reference
 * leaves - a triangle and a sentence per reference (§4), which is not a string
 * and therefore cannot ride `facets`. `renderFooter` inserts it between the
 * line of facts and the stamp, which is where the mockup draws it. Optional,
 * and defaulting to what this file rendered before it, like the two above.
 *
 * ## And a third pill when development waits for production (issue #113)
 *
 * « production v3 » beside « développement v5 » says it only to an eye that
 * compares the two numbers, card after card. So a yellow « À passer en
 * production » follows the two pills when `developmentAhead` holds - the
 * server's fact, the very one behind the « Développement en avance sur la
 * production » filter and the header's own button (#108): no production yet,
 * or a higher number, and never a plain inequality, so a development rolled
 * back behind production gets no pill. Closed on a retired card, as that
 * button is: a gesture the editor refuses is not one to announce here.
 *
 * Yellow on its `-tint` / `-deep` pair, the one every attention pill of the
 * application wears, and an arrow so the pill does not rest on colour alone.
 *
 * ## What the card does NOT say
 *
 * The LIST of referents. §4 puts it in the editor and leaves the count here,
 * and `versionedUsageCountSchema` carries only that count for the same reason:
 * a card stays scannable, and « Utilisé dans 2 protocoles et 1 expérience » is
 * the whole of what the fact has to say at this distance.
 *
 * And a usage of ZERO, which says nothing rather than « utilisé nulle part »
 * (§4) - see the assembly of `line` in the component.
 *
 * ## A retired card is grey AND still a link
 *
 * §4 keeps the retired ones behind a checkbox, greyed, with « Restaurer » on
 * the card. Greyed and not disabled: the editor of a retired entity is
 * readable - that is where its history is - and a card one cannot open would
 * make the restoration the only thing left to do with it. The button is
 * therefore INSIDE the card's frame but OUTSIDE its link, because a button
 * nested in an anchor is a click nobody can aim.
 *
 * ## The link is BUILT BY THE CALLER
 *
 * `renderLink` and not a `to` plus `params`: TanStack Router types the two
 * together against the route tree, so a widened path handed down through a
 * generic component stops type-checking against its own params. The caller
 * writes the one `<Link>` it knows how to write and this file hands it the
 * class it must wear.
 */

export interface VersionedCardTileProps<Card extends VersionedCard> {
  wording: VersionedWording;
  card: Card;
  /** The object's own facets, appended to the usage sentence: « 3 exemples ». */
  facets: readonly string[];
  /**
   * What stands beside the chevron. Absent: the entity's name as an `<h2>`.
   *
   * The persona replaces it with its avatar, its name and its subtitle (§15).
   * Whatever it renders is inside the card's LINK, so it must hold no control.
   */
  renderIdentity?: (card: Card) => ReactNode;
  /** One block between the identity row and the pastilles. The persona's gauge (§15). */
  renderExtra?: (card: Card) => ReactNode;
  /** One block between the line of facts and the stamp. The experience's warnings (§15). */
  renderFooter?: (card: Card) => ReactNode;
  /** The link that opens the editor - the caller builds it, the layer dresses it. */
  renderLink: (props: { className: string; children: ReactNode }) => ReactNode;
  /** Fired by « Restaurer », on a retired card only. */
  onRestore: (card: Card) => void;
  /** True while that restoration is in flight, so the gesture cannot fire twice. */
  restoring: boolean;
  /**
   * Fired by « Dupliquer », with the TAG the copy is taken from (§4.2).
   *
   * Optional, like the three render slots: the layer mounts the « … » menu only
   * for a caller that can answer it, so a screen that has not wired the gesture
   * shows no menu rather than a dead one.
   */
  onDuplicate?: (card: Card, tag: VersionTag) => void;
  /** True while a duplication is in flight, so the gesture cannot fire twice. */
  duplicating?: boolean;
}

const LINK_CLASS = 'flex flex-1 flex-col gap-3.5 p-5 text-noir no-underline hover:border-gray-300';

/**
 * One facet, appended to the usage sentence by a middot.
 *
 * Its own component for ONE reason, and it is not decoration: the separator has
 * to be part of the JSX TEXT, and the whole span has to fit on one line so the
 * formatter cannot break it into a `{' '}` of its own. Chrome measures each text
 * node separately and rounds, so a third node shifts the tail of the sentence by
 * a third of a pixel - enough to make the capture of this screen differ from the
 * one taken before this layer existed, on a line whose characters are identical.
 */
function Facet({ label }: { label: string }) {
  return <span className="text-gray-700"> · {label}</span>;
}

export function VersionedCardTile<Card extends VersionedCard>({
  wording,
  card,
  facets,
  renderIdentity,
  renderExtra,
  renderFooter,
  renderLink,
  onRestore,
  restoring,
  onDuplicate: duplicatable,
  duplicating = false,
}: VersionedCardTileProps<Card>) {
  const retired = card.retiredAt !== null;
  /*
    The vitrine (docs/specs/vitrine.md section 7, issue #192): ONE state, so
    the two pastilles become the plain « v12 » the vitrine plays, the line under
    the card says when that version arrived, and neither the « … » menu nor
    « Restaurer » is drawn - both are writes the server would refuse.
  */
  const vitrine = useIsVitrine();
  const onDuplicate = vitrine ? undefined : duplicatable;
  const playedNumber = card.production?.number ?? card.development.number;
  /*
    The « … » menu and the choice it may open (§4.2, issue #116).

    The trigger is held rather than looked up, because the choice panel is
    `position: fixed` and has to be placed against that exact node - and has to
    hand the focus back to it when it closes.
  */
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [choosing, setChoosing] = useState(false);
  const source: DuplicateSource = {
    production: card.production,
    development: card.development,
    hasPendingDraft: card.hasPendingDraft,
  };
  /*
    The one line of facts, and why it is assembled here rather than written in
    place.

    « Un usage nul ne s'affiche pas » (§4): `usageSentence` answers with an
    empty string when nothing cites the entity, and the card then drops the
    sentence AND the middot that would have followed it - « · 3 exemples » with
    a leading separator would read as a fact whose first half went missing.
    Every part after the first wears the separator, whichever one it is, so the
    card holding usage renders exactly the nodes it rendered before this rule.
  */
  const usage = usageSentence(card.usage);
  const line = usage === '' ? facets : [usage, ...facets];
  return (
    <div
      data-testid={`${wording.entity}-carte`}
      data-retired={retired ? 'true' : 'false'}
      className="relative flex flex-col rounded-md border-2 border-gray-200 bg-white"
    >
      {/*
        OUTSIDE the link and over it, for the reason « Restaurer » is outside
        it: a button nested in an anchor is a click nobody can aim. Absolutely
        placed rather than given a row of its own, so a card keeps the height
        every capture of these four libraries was taken at - the head row below
        yields the width instead.

        Offered on a RETIRED card too (§4.2): duplicating is the way out of a
        restoration that « name-taken » refuses.
      */}
      {onDuplicate === undefined ? null : (
        <div className="absolute top-3 right-3 z-10">
          <ActionMenu
            label={`Gestes sur « ${card.name} »`}
            data-testid={`${wording.entity}-menu`}
            triggerRef={setTrigger}
            items={[
              {
                id: 'duplicate',
                label: 'Dupliquer',
                // `Copy`, the one glyph the whole duplication lot wears - the
                // gutter icon of an in-page item and this entry (§4.2).
                icon: <Copy aria-hidden="true" className="size-4" />,
                disabled: duplicating,
                onSelect: () => {
                  if (duplicateAsks(source)) setChoosing(true);
                  else onDuplicate(card, directDuplicateTag(source));
                },
              },
            ]}
          />
        </div>
      )}
      {choosing && onDuplicate !== undefined ? (
        <DuplicateChoice
          wording={wording}
          name={card.name}
          source={source}
          anchor={trigger}
          onClose={() => setChoosing(false)}
          onPick={(tag) => onDuplicate(card, tag)}
        />
      ) : null}
      {renderLink({
        // The wash of a retired card is on the CONTENT and not on the frame,
        // and that is not a detail: `opacity` creates a stacking context, so a
        // wash on the root would trap the « … » menu and the choice panel
        // inside it - rendered at 60 % and painted UNDER the next card of the
        // grid, on the very card §4.2 offers the gesture for.
        className: retired ? `${LINK_CLASS} opacity-60` : LINK_CLASS,
        children: (
          <>
            {/* `pr-10` clears the « … » that stands over this row's right edge. */}
            <div
              className={`flex items-start justify-between gap-3 ${onDuplicate === undefined ? '' : 'pr-10'}`}
            >
              {renderIdentity === undefined ? (
                <h2 className="min-w-0 break-words font-semibold text-bleu-nuit text-lg leading-7">
                  {card.name}
                </h2>
              ) : (
                renderIdentity(card)
              )}
              {/* The chevron says « this opens », and says it to the eye only: the
                  link's accessible name is the entity's name, and an icon that
                  announced itself would read it out twice. */}
              <ChevronRight aria-hidden="true" className="mt-1 size-5 shrink-0 text-gray-400" />
            </div>

            {renderExtra?.(card)}

            <div className="flex flex-wrap gap-2">
              {vitrine ? (
                <span
                  data-testid={`${wording.entity}-version-jouee`}
                  className="font-semibold text-gray-900 text-sm leading-5"
                >
                  v{playedNumber}
                </span>
              ) : (
                <>
                  {/* ABSENT rather than a placeholder - see `version-pill.tsx`. */}
                  {card.production === null ? null : (
                    <VersionPill
                      entity={wording.entity}
                      tag="production"
                      version={card.production.number}
                    />
                  )}
                  <VersionPill
                    entity={wording.entity}
                    tag="development"
                    version={card.development.number}
                  />
                  {card.developmentAhead && !retired ? (
                    <span
                      data-testid={`${wording.entity}-pastille-a-passer`}
                      className="inline-flex items-center gap-1 rounded-pill bg-jaune-tint px-2.5 py-0.5 font-semibold text-jaune-deep text-xs leading-4"
                    >
                      <ArrowUp aria-hidden="true" className="size-3.5" strokeWidth={2.5} />À passer
                      en production
                    </span>
                  ) : null}
                </>
              )}
            </div>

            {/* No usage, no facets, no line: an empty paragraph would still
                cost the gap above it. */}
            {line.length === 0 ? null : (
              <p className="text-gray-700 text-sm">
                {line[0]}
                {line.slice(1).map((part) => (
                  <Facet key={part} label={part} />
                ))}
              </p>
            )}

            {renderFooter?.(card)}

            {/* `mt-auto` pins the stamp to the bottom edge whatever the name above
                wrapped to, so the six cards of a row read as one register. */}
            <p className="mt-auto pt-1.5 text-gray-600 text-xs leading-4">
              {vitrine
                ? VITRINE_WORDING.arrived(
                    playedNumber,
                    versionedDay(card.lastVersionAt),
                    card.lastVersionBy,
                  )
                : `v${card.development.number} enregistrée le ${versionedDay(card.lastVersionAt)} · ${card.lastVersionBy}`}
            </p>
          </>
        ),
      })}

      {retired ? (
        <div className="flex items-center justify-between gap-3 border-gray-100 border-t px-5 py-3 opacity-60">
          <p className="text-gray-600 text-xs leading-4">
            Retiré le {versionedDay(card.retiredAt ?? '')}
          </p>
          {vitrine ? null : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={restoring}
              onClick={() => onRestore(card)}
            >
              Restaurer
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
